import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ListEditor = ({ title, hint, rows, onChange, withSalary, testid }) => {
  const upd = (i, k, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div><div className="font-semibold text-sm">{title}</div><div className="text-xs text-muted-foreground">{hint}</div></div>
        <Button size="sm" variant="outline" onClick={() => onChange([...rows, { name: '', salary: 0, active: true }])} data-testid={`${testid}-add`}>+ Agregar</Button>
      </div>
      {rows.length === 0 && <p className="text-xs text-slate-400">Sin registros</p>}
      {rows.map((r, i) => (
        <div key={r.id || i} className="flex items-center gap-2" data-testid={`${testid}-row-${i}`}>
          <Input value={r.name} onChange={e => upd(i, 'name', e.target.value)} placeholder="Nombre" className="flex-1 h-9" data-testid={`${testid}-name-${i}`} />
          {withSalary && <Input type="number" min="0" value={r.salary} onChange={e => upd(i, 'salary', e.target.value)} placeholder="Salario" className="w-32 h-9" data-testid={`${testid}-salary-${i}`} />}
          <Switch checked={r.active !== false} onCheckedChange={v => upd(i, 'active', v)} title="Activo" data-testid={`${testid}-active-${i}`} />
          <Button size="sm" variant="ghost" className="text-rose-600 h-8 px-2" onClick={() => onChange(rows.filter((_, j) => j !== i))} data-testid={`${testid}-del-${i}`}>✕</Button>
        </div>
      ))}
    </div>
  );
};

export const ExpenseConfigCard = () => {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (isAdmin) axios.get(`${API}/expense-config`, { withCredentials: true }).then(r => setCfg(r.data)).catch(() => {});
  }, [isAdmin]);

  if (!isAdmin || !cfg) return null;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/expense-config`, {
        types: cfg.types, employees: cfg.employees.map(e => ({ ...e, salary: parseFloat(e.salary) || 0 })),
      }, { withCredentials: true });
      setCfg(data);
      toast.success('Gastos guardados');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  return (
    <Card data-testid="expense-config-card">
      <CardHeader><CardTitle>🧾 Gastos del corte</CardTitle></CardHeader>
      <CardContent className="space-y-6">
        <ListEditor title="Empleados y salarios" hint="Aparecen al elegir Salario. Si pagan distinto al asignado te llega aviso por Telegram."
          rows={cfg.employees} withSalary onChange={(employees) => setCfg({ ...cfg, employees })} testid="expense-employees" />
        <ListEditor title="Tipos de gasto en Otros" hint="Aparecen al elegir Otros (también existe 'Otro' con nota). Cada gasto de Otros te avisa por Telegram."
          rows={cfg.types} onChange={(types) => setCfg({ ...cfg, types })} testid="expense-types" />
        <div className="flex justify-end"><Button onClick={save} disabled={saving} data-testid="expense-config-save">{saving ? 'Guardando…' : 'Guardar'}</Button></div>
      </CardContent>
    </Card>
  );
};
